<script setup lang="ts">
import FlightsOmnisearchPanel from "~/components/home/HeroSearch.vue";
import HomeValuePropositionRow from "~/components/home/ValuePropositionRow.vue";
import HomeTrendingDestinations from "~/components/home/TrendingDestinations.vue";
import SharedAppDownloadBanner from "~/components/shared/AppDownloadBanner.vue";
/**
* ScholarTrip 2.0 - Home Entrance (Production Ready)
* 🚨 [架構修復]：
* 1. 沉浸式 Hero：確保背景圖片涵蓋整個上方視圖 (min-h-screen)。
* 2. 響應式優化：修復折疊手機 (sm) 與平板 (md) 上的文字擁擠與搜尋面板超出邊界問
題。
*/
useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "How to find the best flight deals on ScholarTrip?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "ScholarTrip uses AI to track 80+ airlines in real-time, offering exclusive fares for general travelers and verified student discounts.",
            },
          },
        ],
      }),
    },
  ],
});
useSeoMeta({
  title: "ScholarTrip | Official Flights, Hotels & Global Travel Super App",
  description:
    "Book official airline inventory with AI price insights. Exclusive perks for students and youth explorers.",
});
definePageMeta({
  transparentHeader: true,
});
</script>
<template>
  <div class="bg-slate-50 min-h-screen">
    <!-- 1. 沉浸式 Hero 區塊 (Immersive Hero) -->
    <section
      class="relative min-h-[90vh] lg:min-h-screen flex flex-col items-center justify-center pt-28 pb-20 overflow-hidden bg-slate-900"
    >
      <!-- 背景圖片與漸層遮罩 -->
      <div class="absolute inset-0 z-0">
        <NuxtImg
          src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop"
          class="w-full h-full object-cover opacity-40 scale-105"
          format="webp"
          priority
        />
        <div
          class="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-900/40 to-slate-50"
        ></div>
      </div>
      <div
        class="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center"
      >
        <!-- 核心文案 -->
        <h1
          class="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white mb-6 tracking-tighter leading-[0.95] animate-fade-in-up drop-shadow-2xl"
        >
          Explore the World, <br />
          <span class="text-action-500 italic">Scholar Style.</span>
        </h1>
        <p
          class="text-base sm:text-lg md:text-2xl text-slate-200 font-medium max-w-3xl mx-auto mb-10 md:mb-14 opacity-90 drop-shadow-md px-4 leading-relaxed"
        >
          Live Official Fares + AI Intelligence. <br class="hidden md:block" />
          The ultimate platform for global explorers and students.
        </p>
        <!-- 專業搜尋面板 -->
        <div class="w-full text-start relative z-20">
          <FlightsOmnisearchPanel />
        </div>
      </div>
    </section>
    <!-- 2. 價值主張與企業級保證 -->
    <div class="-mt-8 relative z-20">
      <HomeValuePropositionRow />
    </div>
    <!-- 3. 熱門目的地便當盒網格 -->
    <HomeTrendingDestinations />
    <!-- 4. 多產品快速導航 -->
    <section class="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
      <div
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-5"
      >
        <div
          v-for="p in [
            'Hotels',
            'Packages',
            'Cruises',
            'Cars',
            'Trains',
            'Transfers',
            'Insurance',
            'Visas',
            'Groups',
          ]"
          :key="p"
          class="bg-white p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 hover:border-brand-200 transition-all cursor-pointer group text-center flex flex-col items-center justify-center aspect-square md:aspect-auto"
        >
          <Icon
            name="ph:suitcase-simple-bold"
            class="text-3xl md:text-4xl text-slate-300 group-hover:text-brand-600 mb-3 md:mb-4 transition-colors"
          />
          <p
            class="font-black text-[10px] md:text-xs uppercase tracking-widest text-slate-500 group-hover:text-slate-900 transition-colors"
          >
            {{ p }}
          </p>
        </div>
      </div>
    </section>
    <SharedAppDownloadBanner />
  </div>
</template>
